// client/src/pages/admin/AdminOrderDetail.js
// Week 6 Day 35-37: Order Detail with Admin Intervention
// Admin order detail page with notes, status changes, cancellation
// Location: client/src/pages/admin/AdminOrderDetail.js

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShoppingCart, User, Building2, Package, DollarSign,
  Calendar, Clock, MessageSquare, AlertTriangle, CheckCircle,
  XCircle, Edit, Save, X, FileText, Truck
} from 'lucide-react';
import api from '../../services/api';
import ModificationHistorySection from '../../components/order/ModificationHistorySection';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Admin action modals
  const [showAddNote, setShowAddNote] = useState(false);
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [showCancelOrder, setShowCancelOrder] = useState(false);
  const [showProblemFlag, setShowProblemFlag] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  // Form states
  const [noteText, setNoteText] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [problemNote, setProblemNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      // ✅ FIX: Renamed to 'response' to avoid confusion
      const response = await api.adminAPI.getOrderDetail(id);
      
      // ✅ FIX: Extract .data from the response object
      // The API returns { data: { ...order }, status: 200, ... }
      // We must pass response.data to state, otherwise order.status is 200 (number)
      setOrder(response.data); 
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Error fetching order details: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Add admin note
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      setSubmitting(true);
      await api.adminAPI.addOrderNote(id, { note: noteText });
      setNoteText('');
      setShowAddNote(false);
      await fetchOrderDetails();
      toast.success('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Error adding note: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Change order status
  const handleChangeStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      setSubmitting(true);
      await api.adminAPI.updateOrderStatus(id, {
        status: newStatus,
        reason: statusReason
      });
      setNewStatus('');
      setStatusReason('');
      setShowChangeStatus(false);
      await fetchOrderDetails();
      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Cancel order
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please enter a cancellation reason');
      return;
    }

    setConfirmModal({
      title: 'Cancel Order',
      message: 'Are you sure you want to cancel this order? This action cannot be undone.',
      icon: 'danger',
      confirmLabel: 'Cancel Order',
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setSubmitting(true);
          await api.adminAPI.cancelOrder(id, { reason: cancelReason });
          setCancelReason('');
          setShowCancelOrder(false);
          await fetchOrderDetails();
          toast.success('Order cancelled successfully');
        } catch (error) {
          console.error('Error cancelling order:', error);
          toast.error('Error cancelling order: ' + (error.response?.data?.message || error.message));
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  // Toggle problem flag
  const handleToggleProblem = async () => {
    try {
      setSubmitting(true);
      await api.adminAPI.flagOrderProblem(id, {
        isProblem: !order.isProblem,
        note: problemNote
      });
      setProblemNote('');
      setShowProblemFlag(false);
      await fetchOrderDetails();
      toast.success(`Order ${!order.isProblem ? 'flagged' : 'unflagged'} as problem successfully`);
    } catch (error) {
      console.error('Error toggling problem flag:', error);
      toast.error('Error updating problem flag: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-300';
    
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      processing: 'bg-purple-100 text-purple-800 border-purple-300',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Order not found</p>
          <button
            onClick={() => navigate('/admin/orders')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  // ✅ Safe status helper
  const currentStatus = order.status || 'pending';

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/orders')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Orders</span>
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {order.orderNumber}
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(currentStatus)}`}>
                {/* ✅ FIX: Safe access to charAt now that we are sure it's a string */}
                {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
              </span>
              {order.isProblem && (
                <span className="px-3 py-1 text-sm font-semibold rounded-full border bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Problem Order
                </span>
              )}
            </div>
          </div>

          {/* Admin Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAddNote(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Add Note
            </button>
            <button
              onClick={() => setShowChangeStatus(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Change Status
            </button>
            {order.status !== 'cancelled' && order.status !== 'delivered' && (
              <button
                onClick={() => setShowCancelOrder(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Cancel Order
              </button>
            )}
            <button
              onClick={() => setShowProblemFlag(true)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                order.isProblem
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              {order.isProblem ? 'Unflag' : 'Flag'} Problem
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Total Amount</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  €{(order.totalAmount || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm">Items</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {order.items?.length || 0}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Created</span>
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {order.createdAt ? new Date(order.createdAt).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'N/A'}
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Updated</span>
                </div>
                <p className="text-sm font-medium text-gray-900">
                  {order.updatedAt ? new Date(order.updatedAt).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Business & Wholesaler Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Business */}
            {order.business && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Business</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900">{order.business?.name || 'Unknown'}</p>
                  <p className="text-gray-600">{order.business?.type || '-'}</p>
                  <p className="text-gray-600">{order.business?.contact || '-'}</p>
                  <p className="text-gray-600">{order.business?.email || '-'}</p>
                  <p className="text-gray-600">{order.business?.phone || '-'}</p>
                  <p className="text-gray-600">{order.business?.serviceArea || '-'}</p>
                </div>
              </div>
            )}

            {/* Wholesaler */}
            {order.wholesaler && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Wholesaler</h2>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900">{order.wholesaler?.name || 'Unknown'}</p>
                  <p className="text-gray-600">{order.wholesaler?.contact || '-'}</p>
                  <p className="text-gray-600">{order.wholesaler?.email || '-'}</p>
                  <p className="text-gray-600">{order.wholesaler?.phone || '-'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h2>
            <div className="space-y-3">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name || item.productName}</p>
                      <p className="text-sm text-gray-600">
                        Quantity: {item.quantity} × €{(item.unitPrice || item.price)?.toFixed(2)}
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">
                      €{((item.quantity || 0) * (item.unitPrice || item.price || 0)).toFixed(2)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 text-center py-4">No items in this order</p>
              )}
            </div>
          </div>

          {/* Modification History */}
          <ModificationHistorySection order={order} />

          {/* Status History */}
          {order.statusHistory && order.statusHistory.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Status History</h2>
              <div className="space-y-3">
                {order.statusHistory.map((history, index) => (
                  <div key={index} className="flex gap-3 pb-3 border-b border-gray-200 last:border-0">
                    <div className="flex-shrink-0">
                      {history.status === 'delivered' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : history.status === 'cancelled' ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getStatusColor(history.status)}`}>
                          {history.status.charAt(0).toUpperCase() + history.status.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {history.changedAt ? new Date(history.changedAt).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                      {history.note && (
                        <p className="text-sm text-gray-600 mt-1">{history.note}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Changed by: {history.changedBy}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Admin Notes & Comments */}
        <div className="space-y-6">
          {/* Admin Notes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Admin Notes</h2>
            {order.adminNotes && order.adminNotes.length > 0 ? (
              <div className="space-y-3">
                {order.adminNotes.map((note, index) => (
                  <div key={index} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-gray-900">{note.note}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {note.addedAt ? new Date(note.addedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">No admin notes</p>
            )}
          </div>

          {/* ERP Sync Status */}
          {order.erpMetadata && order.erpMetadata.erpSyncStatus !== 'not_applicable' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">ERP Sync</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    order.erpMetadata.erpSyncStatus === 'synced' ? 'text-green-600' :
                    order.erpMetadata.erpSyncStatus === 'pending' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>{order.erpMetadata.erpSyncStatus}</span>
                </div>
                {order.erpMetadata.erpId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ERP ID:</span>
                    <span className="font-mono">{order.erpMetadata.erpId}</span>
                  </div>
                )}
                {order.erpMetadata.lastSyncAttempt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Attempt:</span>
                    <span>{new Date(order.erpMetadata.lastSyncAttempt).toLocaleString()}</span>
                  </div>
                )}
                {order.erpMetadata.erpError && (
                  <div className="mt-2 p-2 bg-red-50 rounded text-red-700 text-xs">{order.erpMetadata.erpError}</div>
                )}
                {order.erpMetadata.erpSyncStatus === 'failed' && (
                  <button
                    onClick={async () => {
                      try {
                        const { ordersAPI } = await import('../../services/api');
                        const res = await ordersAPI.retryErpSync(order._id);
                        if (res.data.success) {
                          setOrder(prev => ({ ...prev, erpMetadata: { ...prev.erpMetadata, erpSyncStatus: 'synced', erpError: null } }));
                        }
                      } catch {}
                    }}
                    className="w-full mt-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                  >
                    Retry ERP Sync
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Order Comments */}
          {order.comments && order.comments.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Comments</h2>
              <div className="space-y-3">
                {order.comments.map((comment, index) => (
                  <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-900">{comment.message || comment.text || 'No message'}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        comment.author === 'business' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {comment.author === 'business' ? 'Business' : 'Wholesaler'}
                      </span>
                      <p className="text-xs text-gray-500">
                        {comment.authorName || 'Unknown'} - {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoice */}
          {order.invoiceUrl && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice</h2>
              <a
                href={order.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              >
                <FileText className="w-5 h-5" />
                <span>View Invoice</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      {showAddNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Admin Note</h3>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddNote}
                disabled={submitting || !noteText.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {submitting ? 'Adding...' : 'Add Note'}
              </button>
              <button
                onClick={() => {
                  setShowAddNote(false);
                  setNoteText('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showChangeStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Order Status</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select status...</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="Why are you changing the status?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleChangeStatus}
                disabled={submitting || !newStatus}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                {submitting ? 'Updating...' : 'Update Status'}
              </button>
              <button
                onClick={() => {
                  setShowChangeStatus(false);
                  setNewStatus('');
                  setStatusReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Order</h3>
            <p className="text-gray-600 mb-4">
              This will cancel the order. This action cannot be undone.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason *
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why are you cancelling this order?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCancelOrder}
                disabled={submitting || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              >
                {submitting ? 'Cancelling...' : 'Cancel Order'}
              </button>
              <button
                onClick={() => {
                  setShowCancelOrder(false);
                  setCancelReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Flag Modal */}
      {showProblemFlag && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {order.isProblem ? 'Unflag' : 'Flag'} as Problem Order
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (optional)
              </label>
              <textarea
                value={problemNote}
                onChange={(e) => setProblemNote(e.target.value)}
                placeholder="What's the issue?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleToggleProblem}
                disabled={submitting}
                className={`flex-1 px-4 py-2 text-white rounded-lg disabled:bg-gray-400 ${
                  order.isProblem
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {submitting ? 'Updating...' : (order.isProblem ? 'Unflag' : 'Flag') + ' Order'}
              </button>
              <button
                onClick={() => {
                  setShowProblemFlag(false);
                  setProblemNote('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminOrderDetail;